import { useState, useEffect, useRef } from 'react';
import { usePWA } from '../hooks/usePWA';
import { isStandalone, isIOS, pushSupported, subscribePush, reportAppStatus } from '../lib/push';

const DAY = 86400000;
const getTs = (k) => { try { return Number(localStorage.getItem(k)) || 0; } catch (e) { return 0; } };
const setTs = (k) => { try { localStorage.setItem(k, String(Date.now())); } catch (e) { /* */ } };
const GRAD = 'var(--brand-grad)';

// Manifest dinámico: nombre y logo de la organización (si no hay logo, queda el de Gestión360)
function useOrgManifest(orgName, logoUrl) {
  useEffect(() => {
    if (!orgName && !logoUrl) return;
    let url;
    try {
      const o = window.location.origin;
      const icons = logoUrl
        ? [{ src: logoUrl, sizes: '192x192', type: 'image/png', purpose: 'any' }, { src: logoUrl, sizes: '512x512', type: 'image/png', purpose: 'any' }]
        : [{ src: o + '/icon-192.png', sizes: '192x192', type: 'image/png' }, { src: o + '/icon-512.png', sizes: '512x512', type: 'image/png' }];
      const m = {
        name: orgName || 'Gestión360', short_name: (orgName || 'Gestión360').slice(0, 12),
        start_url: o + '/', scope: o + '/', display: 'standalone', lang: 'es',
        background_color: '#222d5a', theme_color: '#222d5a', icons,
      };
      url = URL.createObjectURL(new Blob([JSON.stringify(m)], { type: 'application/manifest+json' }));
      const link = document.querySelector('link[rel="manifest"]');
      if (link) link.setAttribute('href', url);
    } catch (e) { /* queda el manifest estático */ }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [orgName, logoUrl]);
}

export default function PWABanner({ orgName, logoUrl }) {
  const { canInstall, install, notifPermission, requestNotifications } = usePWA();
  const [perm, setPerm] = useState(notifPermission);
  const [hideInstall, setHideInstall] = useState(() => Date.now() - getTs('g360_install_hidden') < 2 * DAY);
  const [askNotif, setAskNotif] = useState(false);
  const [iosGuide, setIosGuide] = useState(false);
  const [busy, setBusy] = useState(false);
  const standalone = isStandalone();
  const ios = isIOS();
  const reported = useRef(false);

  useOrgManifest(orgName, logoUrl);

  // Dentro de la app: informar estado, suscribir si ya hay permiso y pedir permiso (se repite cada 2 días si lo rechaza)
  useEffect(() => {
    if (!standalone) return;
    if (!reported.current) { reported.current = true; reportAppStatus(); }
    if (Notification?.permission === 'granted') subscribePush().catch(() => {});
    if (pushSupported() && Notification.permission === 'default' && Date.now() - getTs('g360_notif_asked') > 2 * DAY) {
      const t = setTimeout(() => setAskNotif(true), 2500);
      return () => clearTimeout(t);
    }
  }, [standalone]);

  const accept = async () => {
    setBusy(true);
    const ok = await requestNotifications();
    setPerm(Notification.permission);
    if (ok) { await subscribePush().catch(() => {}); }
    await reportAppStatus();
    setTs('g360_notif_asked');
    setBusy(false); setAskNotif(false);
  };
  const later = () => { setTs('g360_notif_asked'); setAskNotif(false); };

  const doInstall = async () => {
    setBusy(true);
    const ok = await install();
    setBusy(false);
    if (!ok) { setTs('g360_install_hidden'); setHideInstall(true); }
  };

  // ── En la app: pedido de notificaciones
  if (standalone) {
    if (!askNotif || perm !== 'default') return null;
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 p-4">
        <div className="max-w-lg mx-auto bg-white rounded-3xl shadow-2xl border border-gray-100 p-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 text-2xl bg-amber-50">🔔</div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 text-sm">Activá las notificaciones</p>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Te avisamos de tus horarios, adelantos, pagos y novedades, aunque no tengas la app abierta.</p>
              <div className="flex gap-2">
                <button onClick={accept} disabled={busy} className="flex-1 py-2.5 rounded-2xl text-sm font-bold text-white disabled:opacity-60" style={{ background: GRAD }}>Activar</button>
                <button onClick={later} className="px-4 py-2.5 rounded-2xl text-sm font-semibold text-gray-400 hover:bg-gray-50">Más tarde</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── En la web: botón para instalar (solo si el navegador lo permite o en iPhone)
  if (hideInstall || (!canInstall && !ios)) return null;
  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40 p-3 pointer-events-none">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-2.5 flex items-center gap-2 pointer-events-auto">
          {logoUrl
            ? <img src={logoUrl} alt="" className="w-10 h-10 rounded-xl object-contain bg-gray-50 flex-shrink-0" />
            : <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold" style={{ background: GRAD }}>G</div>}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">Instalá la app</p>
            <p className="text-[11px] text-gray-400 truncate">Acceso directo y notificaciones</p>
          </div>
          <button onClick={ios ? () => setIosGuide(true) : doInstall} disabled={busy} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 active:scale-95 transition-all" style={{ background: GRAD }}>
            {busy ? '…' : 'Instalar'}
          </button>
          <button onClick={() => { setTs('g360_install_hidden'); setHideInstall(true); }} className="text-gray-300 hover:text-gray-500 px-1.5" aria-label="Cerrar">✕</button>
        </div>
      </div>
      {iosGuide && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIosGuide(false)} />
          <div className="relative bg-white w-full max-w-lg rounded-t-3xl shadow-2xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Instalar en iPhone / iPad</h2>
            {[['1', '⬆️', 'Tocá el botón compartir', 'El cuadrado con flecha hacia arriba, en la barra de Safari'],
              ['2', '📋', 'Elegí "Agregar a pantalla de inicio"', 'Desplazá hacia abajo en el menú'],
              ['3', '🔔', 'Abrí la app desde el ícono', 'Ahí te pedimos permiso para las notificaciones']].map(([n, i, t, d]) => (
              <div key={n} className="flex items-start gap-3 bg-gray-50 rounded-2xl p-4">
                <span className="text-2xl flex-shrink-0">{i}</span>
                <div><p className="text-sm font-bold text-gray-900">{t}</p><p className="text-xs text-gray-500 mt-0.5">{d}</p></div>
              </div>
            ))}
            <button onClick={() => setIosGuide(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white" style={{ background: GRAD }}>Entendido</button>
          </div>
        </div>
      )}
    </>
  );
}
